import React from 'react';
import {
  Detail,
  DetailContainer,
  DetailContainerTitle,
  DetailContentRow
} from './DetailCard/styles';
import { DetailCard } from './DetailCard';
import { cnpjMask, formatAddress } from '../../../../utils/formatStrings';

import { Icon } from '@gerdau/hefesto/Icon';
import { ContentToggle, ResumeHeaderWrapper } from './styles';
import {
  INITIAL_RESUME_DETAILS,
  SalesOrderItemTable
} from '../../../../store/ducks/new-long-steel-orders/types';
import {
  formatCurrencyValue,
  isUnavailable,
  toNumberOrZero,
  valueValidation
} from '../../../../store/ducks/new-long-steel-orders';
import { ProgressVolumeBar } from '../ProgressVolumeBar';
import { useDispatch, useSelector } from 'react-redux';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ApplicationState } from '../../../../store';
import { EOrderTab } from '../../../../store/ducks/long-steel-orders/types';
import moment from 'moment';

interface Props {
  data: SalesOrderItemTable[] | null;
}

function statusOrder({
  data,
  countOverdue
}: {
  data: string[];
  countOverdue: number;
}): string {
  if (data.length === 0) return '';

  if (countOverdue > 0) {
    if (countOverdue === 1) {
      return '1 entrega em atraso';
    }

    return `${countOverdue} entregas em atraso`;
  }

  const countMap: Record<string, number> = {};

  for (const item of data) {
    countMap[item] = (countMap[item] || 0) + 1;
  }

  let mostFrequent = data[0];
  let maxCount = 0;

  for (const [key, value] of Object.entries(countMap)) {
    if (value > maxCount) {
      mostFrequent = key;
      maxCount = value;
    }
  }

  return mostFrequent;
}

export const ResumeSection = ({ data }: Props) => {
  if (!data) {
    return null;
  }

  const dispatch = useDispatch();

  const resume = data.reduce((acc, item, idx) => {
    const totalQuantityRequested =
      acc.totalQuantityRequested +
      toNumberOrZero(item?.quantities?.requestedConversionQuantity);

    const totalQuantityPending =
      acc.totalQuantityPending +
      toNumberOrZero(item?.quantities?.pendingConversionQuantity);

    const totalQuantityBilled =
      acc.totalQuantityBilled +
      toNumberOrZero(
        item?.billing?.reduce(
          (itmAcc, itmItem) =>
            itmAcc + (Number(itmItem.billedConversionQuantity) || 0),
          0
        )
      );

    const itemIsFullyBilled =
      Number(item.quantities?.pendingConversionQuantity || 0) <= 0 &&
      Number(item.quantities?.billedConversionQuantity || 0) > 0;

    const itemTotalWithoutTaxes =
      Number(
        itemIsFullyBilled
          ? item.conditions.invoiceTotalValueWithoutTaxes
          : item.conditions.totalValueWithoutTaxes
      ) || 0;

    const itemTotalWithTaxes =
      Number(
        itemIsFullyBilled
          ? item.conditions.invoiceTotalValueWithTaxes
          : item.conditions.totalValueWithTaxes
      ) || 0;

    const quantities = {
      billed: {
        quantity:
          Number(acc.quantities.billed.quantity) +
          Number(
            toNumberOrZero(
              item?.billing?.reduce(
                (itmAcc, itmItem) =>
                  itmAcc + (Number(itmItem.billedConversionQuantity) || 0),
                0
              )
            )
          ),
        measureUnit: 'T'
      },
      delivery: {
        quantity:
          Number(acc.quantities.delivery.quantity) +
          Number(item.quantities.deliveryConversionQuantity || 0),
        measureUnit: 'T'
      },
      shipment: {
        quantity:
          Number(acc.quantities.shipment.quantity) +
          Number(item.quantities.shipmentConversionQuantity || 0),
        measureUnit: 'T'
      },
      pending: {
        quantity:
          Number(acc.quantities.pending.quantity) +
          Number(item.quantities.pendingConversionQuantity || 0),
        measureUnit: 'T'
      },
      requested: {
        quantity:
          Number(acc.quantities.requested.quantity) +
          Number(item.quantities.requestedConversionQuantity || 0),
        measureUnit: 'T'
      }
    };

    return {
      ...acc,
      ...(idx === 0 && { creationDate: item.creationDateTable }),
      ...(idx === 0 && { soldTo: item.soldTo }),
      ...(idx === 0 && { shipTo: item.shipTo }),
      ...(idx === 0 && {
        requestedMeasure: 'T' //Os valores convertidos são em toneladas conforme dicionário do backend
      }),
      totalValueWithoutTaxes: (
        (Number(acc.totalValueWithoutTaxes) || 0) + itemTotalWithoutTaxes
      ).toFixed(2),
      totalValueWithTaxes: (
        (Number(acc.totalValueWithTaxes) || 0) + itemTotalWithTaxes
      ).toFixed(2),
      ...(idx === 0 && { st: valueValidation(item.conditions.totalOrderSt) }),
      ...(idx === 0 && {
        icms: valueValidation(item.conditions.totalOrderIcms)
      }),
      ...(idx === 0 && {
        pis: valueValidation(item.conditions.totalOrderPis)
      }),
      ...(idx === 0 && {
        ipi: valueValidation(item.conditions.totalOrderIpi)
      }),
      ...(idx === 0 && {
        cofins: valueValidation(item.conditions.totalorderCofins)
      }),
      totalQuantityRequested,
      totalQuantityPending,
      totalQuantityBilled,
      quantities
    };
  }, INITIAL_RESUME_DETAILS);

  const [showContent, setShowContent] = React.useState(false);

  const countOverdue = data.filter(
    (item) => item.futureDeliveriesTable === 'Com atraso'
  ).length;

  const status = statusOrder({
    data: data.map((item) => item.futureDeliveriesTable),
    countOverdue: countOverdue
  });

  const ordersState = useSelector(
    (state: ApplicationState) => state.newLongSteelOrders
  );

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );

  const sendExpandingTagging = () => {
    if (showContent) {
      dispatch(
        sendTagging({
          event: 'expandir_detalhes_pedido',
          pedido_id: data[0].id,
          screenName: 'LG:Detalhes_do_pedido',
          itemDescription: 'meus_pedidos_acosLongos',
          estado_emissor:
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.states.join(', '),
          emissor_pedido: customersState.customers
            .filter((item) =>
              ordersState[
                ordersState.tableType || EOrderTab.OPEN
              ].initialInputValues.emitterIds.includes(item.customerId)
            )
            .map((item) => item.name)
            .join(', '),
          data_inicio:
            ordersState.tableType === EOrderTab.OPEN
              ? undefined
              : moment(
                  ordersState[ordersState.tableType || EOrderTab.OPEN]
                    .initialInputValues.startDate
                ).format('DD/MM/YYYY'),
          data_fim:
            ordersState.tableType === EOrderTab.OPEN
              ? undefined
              : moment(
                  ordersState[ordersState.tableType || EOrderTab.OPEN]
                    .initialInputValues.endDate
                ).format('DD/MM/YYYY')
        })
      );
    }
  };

  return (
    <Detail showContent={showContent}>
      <DetailContainer>
        <ResumeHeaderWrapper>
          <DetailContainerTitle>Resumo da ordem de venda</DetailContainerTitle>
          <ContentToggle
            fullWidth
            onClick={() => {
              setShowContent((prev) => !prev);
              sendExpandingTagging();
            }}
          >
            {showContent ? 'Ocultar detalhes' : 'Mostrar detalhes'}{' '}
            <Icon
              option={showContent ? 'ChevronUp' : 'ChevronDown'}
              size="sm"
              variant="primary"
            />
          </ContentToggle>
        </ResumeHeaderWrapper>
        <DetailContentRow cols={4} graphCols>
          <DetailCard
            {...{
              title: 'Data de criação',
              subtitle: resume.creationDate ?? ' - '
            }}
          />

          <DetailCard
            {...{
              withoutSubtitle: true,
              title: 'Status do pedido'
            }}
          >
            <ProgressVolumeBar
              requestedSubtitle="Qtd. total solicitada:"
              {...resume.quantities}
            />
          </DetailCard>

          <DetailCard
            {...{
              title: 'Status do pedido',
              futureDeliveriesTable: status,
              withTag: true,
              countOverdue: countOverdue > 0,
              countDeliveryOverdue: true
            }}
          />
        </DetailContentRow>
      </DetailContainer>
      <DetailContainer>
        <DetailContainerTitle>Detalhes do pedido</DetailContainerTitle>
        <DetailContentRow cols={4}>
          <DetailCard
            {...{
              title: 'Emissor do pedido',
              subtitle: resume.soldTo.name
            }}
          />
          <DetailCard
            {...{
              title: 'CNPJ do emissor',
              subtitle: cnpjMask(resume.soldTo.taxNumber)
            }}
          />
          <DetailCard
            {...{
              title: 'Endereço do emissor',
              subtitle: formatAddress([
                resume.soldTo.address,
                resume.soldTo.country,
                resume.soldTo.city,
                resume.soldTo.zipCode
              ])
            }}
          />
          <DetailCard
            {...{
              title: 'Nº do cliente emissor',
              subtitle: resume.soldTo.code
            }}
          />
        </DetailContentRow>
        <DetailContentRow cols={4}>
          <DetailCard
            {...{
              title: 'Recebedor do pedido',
              subtitle: resume.shipTo.name
            }}
          />
          <DetailCard
            {...{
              title: 'CNPJ do recebedor',
              subtitle: cnpjMask(resume.shipTo.taxNumber)
            }}
          />
          <DetailCard
            {...{
              title: 'Endereço do recebedor',
              subtitle: formatAddress([
                resume.shipTo.address,
                resume.shipTo.country,
                resume.shipTo.city,
                resume.shipTo.zipCode
              ])
            }}
          />
          <DetailCard
            {...{
              title: 'Nº do cliente recebedor',
              subtitle: resume.shipTo.code
            }}
          />
        </DetailContentRow>
      </DetailContainer>
      <DetailContainer
        {...([
          resume.totalValueWithTaxes,
          resume.totalValueWithoutTaxes,
          resume.icms,
          resume.pis,
          resume.cofins,
          resume.ipi,
          resume.st
        ].every((item) => item === 'Indisponível') && {
          hidden: true
        })}
      >
        <DetailContainerTitle>Valores do pedido</DetailContainerTitle>
        <DetailContentRow cols={2}>
          <DetailCard
            {...{
              title: 'Preço total do pedido',
              subtitle: formatCurrencyValue(resume.totalValueWithTaxes),
              titleTooltip:
                'O preço total do pedido considera todos os valores do seu pedido, inclusive impostos e descontos.',
              canShow: !isUnavailable(resume.totalValueWithTaxes)
            }}
          />
          <DetailCard
            {...{
              title: 'Preço total sem impostos',
              subtitle: formatCurrencyValue(resume.totalValueWithoutTaxes),
              titleTooltip:
                'O preço total sem impostos considera todos os valores do seu pedido, inclusive descontos mas excluindo impostos.',
              canShow: !isUnavailable(resume.totalValueWithoutTaxes)
            }}
          />
        </DetailContentRow>
      </DetailContainer>
      <DetailContainer
        {...([
          resume.icms,
          resume.pis,
          resume.cofins,
          resume.ipi,
          resume.st
        ].every((item) => item === 'Indisponível') && {
          hidden: true
        })}
      >
        <DetailContainerTitle normal>
          Valores de impostos inclusos no preço total da ordem de venda. Os
          valores de impostos e totais podem sofrer alterações
        </DetailContainerTitle>
        <DetailContentRow cols={4}>
          <DetailCard
            {...{
              title: 'ICMS',
              subtitle: formatCurrencyValue(resume.icms),
              canShow: !isUnavailable(resume.icms)
            }}
          />
          <DetailCard
            {...{
              title: 'PIS',
              subtitle: formatCurrencyValue(resume.pis),
              canShow: !isUnavailable(resume.pis)
            }}
          />
          <DetailCard
            {...{
              title: 'COFINS',
              subtitle: formatCurrencyValue(resume.cofins),
              canShow: !isUnavailable(resume.cofins)
            }}
          />
          <DetailCard
            {...{
              title: 'IPI',
              subtitle: formatCurrencyValue(resume.ipi),
              canShow: !isUnavailable(resume.ipi)
            }}
          />
        </DetailContentRow>
        <DetailContentRow cols={4}>
          {/* <DetailCard
            {...{
              title: 'ISS',
              subtitle: 'Não tem ISS no response'
            }}
          /> */}
          <DetailCard
            {...{
              title: 'ST',
              subtitle: formatCurrencyValue(resume.st),
              canShow: !isUnavailable(resume.st)
            }}
          />
          {/* <DetailCard
            {...{
              title: 'FCP',
              subtitle: 'Não tem FCP no response'
            }}
          />
          <DetailCard
            {...{
              title: 'DIFAL',
              subtitle: 'Não tem DIFAL no response'
            }}
          /> */}
        </DetailContentRow>
      </DetailContainer>
    </Detail>
  );
};
